import { useCallback, useEffect } from 'react';
import { message } from 'antd';
import { useAppDispatch, useAppSelector } from '../store/store';
import {
  fetchMutedEntities,
  muteEntity,
  mutedEntitiesSelectors,
  unmuteEntity
} from '../store/slices/mutedEntities';
import type { MutedEntityType } from '../services/mutes';

type UseMutedEntityOptions = {
  label?: string;
  initialSource?: string;
};

type ToggleOptions = {
  source?: string;
  notes?: string | null;
};

export const useMutedEntity = (
  entityType: MutedEntityType,
  entityId?: string | null,
  options?: UseMutedEntityOptions
) => {
  const dispatch = useAppDispatch();
  const status = useAppSelector(mutedEntitiesSelectors.selectMutedEntitiesStatus);
  const shouldRefresh = useAppSelector(mutedEntitiesSelectors.selectMutedEntitiesShouldRefresh);

  useEffect(() => {
    if (status === 'loading') return;
    if (status === 'idle' || shouldRefresh) {
      dispatch(fetchMutedEntities()).catch(() => {});
    }
  }, [dispatch, shouldRefresh, status]);

  const effectiveId = typeof entityId === 'string' ? entityId.trim() : '';

  const muted = useAppSelector((state) =>
    effectiveId
      ? mutedEntitiesSelectors.selectIsEntityMuted(state, entityType, effectiveId)
      : false
  );
  const pending = useAppSelector((state) =>
    effectiveId
      ? mutedEntitiesSelectors.selectMutePending(state, entityType, effectiveId)
      : false
  );

  const label = options?.label ?? entityId ?? 'Selection';

  const notifyMute = useCallback(() => {
    message.success(`Muted ${label}. Manage mutes in Settings.`);
  }, [label]);

  const notifyUnmute = useCallback(() => {
    message.success({
      content: `Unmuted ${label}.`
    });
  }, [label]);

  const notifyError = useCallback(
    (action: 'mute' | 'unmute') => {
      message.error(
        action === 'mute'
          ? `We couldn't mute ${label}. Please try again.`
          : `We couldn't unmute ${label}. Please try again.`
      );
    },
    [label]
  );

  const mute = useCallback(
    async (override?: ToggleOptions) => {
      if (!effectiveId || pending) return;
      const source = override?.source ?? options?.initialSource;
      const rawNotes = override?.notes;
      const normalizedNotes =
        typeof rawNotes === 'string' ? rawNotes.trim() : typeof rawNotes === 'undefined' ? undefined : null;
      const notes = typeof normalizedNotes === 'string' && normalizedNotes.length > 0 ? normalizedNotes : undefined;
      try {
        await dispatch(
          muteEntity({
            entityType,
            entityId: effectiveId,
            source,
            notes
          })
        ).unwrap();
        notifyMute();
      } catch (error) {
        console.warn('[mute] failed', error);
        notifyError('mute');
      }
    },
    [dispatch, entityType, effectiveId, notifyError, notifyMute, options?.initialSource, pending]
  );

  const unmute = useCallback(
    async (override?: ToggleOptions) => {
      if (!effectiveId || pending) return;
      const source = override?.source ?? options?.initialSource;
      try {
        await dispatch(
          unmuteEntity({
            entityType,
            entityId: effectiveId,
            source
          })
        ).unwrap();
        notifyUnmute();
      } catch (error) {
        console.warn('[unmute] failed', error);
        notifyError('unmute');
      }
    },
    [dispatch, entityType, effectiveId, notifyError, notifyUnmute, options?.initialSource, pending]
  );

  const toggle = useCallback(
    async (override?: ToggleOptions) => {
      if (muted) {
        await unmute(override);
      } else {
        await mute(override);
      }
    },
    [muted, mute, unmute]
  );

  return {
    isMuted: muted,
    pending,
    mute,
    unmute,
    toggle
  };
};

export type UseMutedEntityReturn = ReturnType<typeof useMutedEntity>;
